/**
 * LIVE ACTIVITY DERIVATION
 *
 * Pure helpers that turn an AiChatSession into a human-readable "what is the
 * agent doing right now" description, plus live token estimation. No React,
 * no side effects — fully unit-testable.
 */

import type { AiChatSession, DerivedTool } from "@/types/ai-chat";

export type LiveActivityKind = "thinking" | "tool" | "responding";

export interface LiveActivity {
  kind: LiveActivityKind;
  label: string;
}

const TOOL_VERBS: Array<{ match: RegExp; verb: string }> = [
  { match: /read|view|cat|open/, verb: "Reading" },
  { match: /grep|glob|search|find|ls|list/, verb: "Searching" },
  { match: /edit|patch|replace|update/, verb: "Editing" },
  { match: /write|create/, verb: "Writing" },
  { match: /bash|command|exec|terminal|shell|run/, verb: "Running" },
  { match: /fetch|web|http|browse/, verb: "Fetching" },
  { match: /task|agent|dispatch/, verb: "Delegating" },
];

export function describeTool(tool: DerivedTool): string {
  const name = tool.toolName.toLowerCase();
  const verb = TOOL_VERBS.find((t) => t.match.test(name))?.verb;
  const target = tool.inputSummary?.trim();
  if (verb) return target ? `${verb} ${target}` : verb;
  return target ? `${tool.toolName}: ${target}` : `Using ${tool.toolName}`;
}

export function deriveLiveActivity(session: AiChatSession): LiveActivity {
  // Most recently started tool that is still running wins.
  for (let i = session.toolActivity.length - 1; i >= 0; i--) {
    const tool = session.toolActivity[i];
    if (tool.status === "running") {
      return { kind: "tool", label: describeTool(tool) };
    }
  }

  const last = session.messages[session.messages.length - 1];
  if (last && last.role === "assistant" && last.isStreaming) {
    return { kind: "responding", label: "Writing response" };
  }

  return { kind: "thinking", label: "Thinking" };
}

/**
 * Live token count: accumulated totals from past `cost` events, plus a rough
 * ~4-chars-per-token estimate for the assistant text currently streaming
 * (its cost event hasn't arrived yet). Keeps the counter moving mid-turn.
 */
export function estimateLiveTokens(session: AiChatSession): number {
  let total = session.totalInputTokens + session.totalOutputTokens;
  const last = session.messages[session.messages.length - 1];
  if (last && last.role === "assistant" && last.isStreaming) {
    total += Math.ceil(last.content.length / 4);
  }
  return total;
}

export function formatTokens(n: number): string {
  if (n < 1000) return `${n}`;
  if (n < 10_000) return `${(n / 1000).toFixed(1)}k`;
  return `${Math.round(n / 1000)}k`;
}
